import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { useToast } from '../hooks/use-toast';
import { services, gallery, testimonials, pricingOptions, inspirationGallery, transformations } from '../mock';
import { format } from 'date-fns';
import axios from 'axios';
import { 
  ArrowRight, 
  Phone, 
  Mail, 
  MapPin, 
  Check,
  Paintbrush,
  Package,
  Wrench,
  Layers,
  Sparkles,
  Star,
  Shield,
  Clock,
  Award,
  Calculator,
  Settings,
  CalendarIcon
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const iconMap = {
  paintbrush: Paintbrush,
  package: Package,
  wrench: Wrench,
  layers: Layers,
  sparkles: Sparkles,
  settings: Settings
};

const Home = () => {
  const { toast } = useToast();
  const [quoteForm, setQuoteForm] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    projectSize: '',
    message: '',
    appointmentDate: null
  });
  
  const [photos, setPhotos] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [calculatorData, setCalculatorData] = useState({
    service: '',
    quantity: 1
  });

  // Smooth scroll function
  const scrollToSection = (sectionId) => {
    const element = document.querySelector(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!quoteForm.name || !quoteForm.email || !quoteForm.phone || !quoteForm.service) {
      toast({
        title: "⚠️ Please Fill Required Fields",
        description: "Name, Email, Phone, and Service are required.",
        variant: "destructive"
      });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('name', quoteForm.name);
      formData.append('email', quoteForm.email);
      formData.append('phone', quoteForm.phone);
      formData.append('service', quoteForm.service);
      
      if (quoteForm.projectSize) {
        formData.append('project_size', quoteForm.projectSize);
      }
      
      if (quoteForm.appointmentDate) {
        formData.append('appointment_date', format(quoteForm.appointmentDate, 'PPP'));
      }
      
      if (quoteForm.message) {
        formData.append('message', quoteForm.message);
      }
      
      // Attach photos
      photos.forEach((photo) => {
        formData.append('photos', photo);
      });

      console.log('Sending quote with', photos.length, 'photos');

      // Send to backend API
      const response = await axios.post(`${BACKEND_URL}/api/send-quote`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      console.log('Backend Response:', response.data);

      // Show success message
      const photoText = photos.length > 0 ? ` with ${photos.length} photo(s)` : '';
      toast({
        title: "✅ Quote Request Sent!",
        description: `We'll contact you within 24 hours${photoText}`,
      });

      // Clear form
      setQuoteForm({ 
        name: '', 
        email: '', 
        phone: '', 
        service: '', 
        projectSize: '', 
        message: '', 
        appointmentDate: null
      });
      setPhotos([]);
      
      // Clear file input
      const fileInput = document.getElementById('photos');
      if (fileInput) fileInput.value = '';

    } catch (error) {
      console.error('Quote submission error:', error);
      toast({
        title: "❌ Submission Failed",
        description: "Please call us directly at (248) 383-5867 or try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field, value) => {
    setQuoteForm(prev => ({ ...prev, [field]: value }));
  };

  const handlePhotoChange = (e) => {
    const files = Array.from(e.target.files);
    
    // Validate file count
    if (files.length > 7) {
      toast({
        title: "⚠️ Too Many Photos",
        description: "Maximum 7 photos allowed",
        variant: "destructive"
      });
      return;
    }
    
    // Validate file sizes (5MB max)
    const oversizedFiles = files.filter(file => file.size > 5 * 1024 * 1024);
    if (oversizedFiles.length > 0) {
      toast({
        title: "⚠️ File Too Large",
        description: "Each photo must be under 5MB",
        variant: "destructive"
      });
      return;
    }
    
    setPhotos(files);
  };

  const removePhoto = (index) => {
    setPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const calculatePrice = () => {
    const selected = pricingOptions.find(opt => opt.value === calculatorData.service);
    if (!selected || !calculatorData.quantity) return 0;
    return selected.rate * calculatorData.quantity;
  };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-200">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src="https://customer-assets.emergentagent.com/job_cabinet-hero/artifacts/3942icok_image.png" alt="Cabinet Hero" className="h-16 w-16" />
              <span className="text-2xl font-bold text-[#1e3a8a]">Cabinet Hero</span>
            </div>
            <nav className="hidden md:flex items-center gap-8">
              <a href="#services" onClick={(e) => { e.preventDefault(); scrollToSection('#services'); }} className="text-gray-700 hover:text-[#1e3a8a] transition-colors font-medium cursor-pointer">Services</a>
              <a href="#gallery" onClick={(e) => { e.preventDefault(); scrollToSection('#gallery'); }} className="text-gray-700 hover:text-[#1e3a8a] transition-colors font-medium cursor-pointer">Gallery</a>
              <a href="#quote" onClick={(e) => { e.preventDefault(); scrollToSection('#quote'); }} className="text-gray-700 hover:text-[#1e3a8a] transition-colors font-medium cursor-pointer">Get Quote</a>
              <Button onClick={() => scrollToSection('#quote')} className="bg-[#059669] hover:bg-[#047857] text-white transition-all">
                <Phone className="w-4 h-4 mr-2" />
                Contact Us
              </Button>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-b from-[#f8fafc] to-white py-24">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-6 bg-[#059669] text-white hover:bg-[#047857]">
              CNC Precision • Professional Results
            </Badge>
            <h1 className="text-5xl md:text-6xl font-bold text-[#1e3a8a] mb-6 leading-tight">
              Repair. Replace. <span className="text-[#059669]">Refinish.</span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Transform your kitchen with CNC-crafted cabinet solutions. Fast, affordable, and eco-friendly—
              restoring beauty and function with precision and custom craftsmanship.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                onClick={() => scrollToSection('#quote')}
                className="bg-[#1e3a8a] hover:bg-[#1e40af] text-white text-lg px-8 transition-all"
              >
                Get Free Quote <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                onClick={() => scrollToSection('#gallery')}
                className="border-2 border-[#1e3a8a] text-[#1e3a8a] hover:bg-[#1e3a8a] hover:text-white text-lg px-8 transition-all"
              >
                View Gallery
              </Button>
            </div>
            
            {/* Trust Indicators */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16">
              <div className="flex flex-col items-center gap-3">
                <div className="w-16 h-16 rounded-full bg-[#059669]/10 flex items-center justify-center">
                  <Shield className="w-8 h-8 text-[#059669]" />
                </div>
                <h3 className="font-semibold text-[#1e3a8a]">Licensed & Insured</h3>
                <p className="text-gray-600 text-sm">Professional service you can trust</p>
              </div>
              <div className="flex flex-col items-center gap-3">
                <div className="w-16 h-16 rounded-full bg-[#059669]/10 flex items-center justify-center">
                  <Clock className="w-8 h-8 text-[#059669]" />
                </div>
                <h3 className="font-semibold text-[#1e3a8a]">Fast Turnaround</h3>
                <p className="text-gray-600 text-sm">Projects as quick as 2 days</p>
              </div>
              <div className="flex flex-col items-center gap-3">
                <div className="w-16 h-16 rounded-full bg-[#059669]/10 flex items-center justify-center">
                  <Award className="w-8 h-8 text-[#059669]" />
                </div>
                <h3 className="font-semibold text-[#1e3a8a]">CNC Precision</h3>
                <p className="text-gray-600 text-sm">Perfect fit, every time</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#1e3a8a] mb-4">Our Services</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Comprehensive cabinet solutions powered by CNC technology and expert craftsmanship
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
            {services.map((service) => {
              const Icon = iconMap[service.icon];
              return (
                <Card key={service.id} className="border-2 hover:border-[#059669] transition-all hover:shadow-lg group">
                  <CardHeader>
                    <div className="w-14 h-14 rounded-lg bg-[#1e3a8a]/10 flex items-center justify-center mb-4 group-hover:bg-[#059669]/10 transition-colors">
                      <Icon className="w-7 h-7 text-[#1e3a8a] group-hover:text-[#059669] transition-colors" />
                    </div>
                    <CardTitle className="text-xl text-[#1e3a8a]">{service.title}</CardTitle>
                    <CardDescription className="text-base">{service.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Starting at</span>
                        <span className="text-lg font-bold text-[#059669]">{service.price}</span>
                      </div>
                      <div className="border-t pt-4">
                        <p className="text-sm font-semibold text-gray-700 mb-2">Includes:</p>
                        <ul className="space-y-2">
                          {service.features.map((feature, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-sm text-gray-600">
                              <Check className="w-4 h-4 text-[#059669] mt-0.5 flex-shrink-0" />
                              {feature}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <Button 
                        onClick={() => scrollToSection('#quote')}
                        className="w-full bg-[#1e3a8a] hover:bg-[#1e40af] transition-colors"
                      >
                        Get Quote
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Inspiration Gallery */}
      <section id="gallery" className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#1e3a8a] mb-4">Cabinet Design Inspiration</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Explore popular cabinet styles we can create for your Metro Detroit home
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {inspirationGallery.map((item) => (
              <Card key={item.id} className="overflow-hidden group hover:shadow-xl transition-all border-2">
                <div className="relative overflow-hidden">
                  <img 
                    src={item.image} 
                    alt={item.title} 
                    className="w-full h-80 object-cover group-hover:scale-105 transition-transform duration-300" 
                  />
                  <Badge className="absolute top-3 right-3 bg-[#1e3a8a] text-white">
                    {item.category}
                  </Badge>
                </div>
                <CardContent className="p-6">
                  <h3 className="font-semibold text-xl text-[#1e3a8a] mb-2">{item.title}</h3>
                  <p className="text-gray-600 mb-4">{item.style}</p>
                  <Button 
                    onClick={() => scrollToSection('#quote')}
                    className="w-full bg-[#059669] hover:bg-[#047857] transition-colors"
                  >
                    Get This Style <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <p className="text-gray-600 mb-6">Ready to transform your kitchen?</p>
            <Button 
              size="lg" 
              onClick={() => scrollToSection('#quote')}
              className="bg-[#1e3a8a] hover:bg-[#1e40af] text-lg px-8"
            >
              Schedule Free Consultation <ArrowRight className="ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#1e3a8a] mb-4">What Our Clients Say</h2>
            <p className="text-xl text-gray-600">Join hundreds of satisfied homeowners</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="border-2">
                <CardContent className="p-6">
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-[#f59e0b] text-[#f59e0b]" />
                    ))}
                  </div>
                  <p className="text-gray-700 mb-6 italic">"{testimonial.text}"</p>
                  <div>
                    <p className="font-semibold text-[#1e3a8a]">{testimonial.name}</p>
                    <p className="text-sm text-gray-600">{testimonial.location}</p>
                    <Badge className="mt-2 bg-[#059669]/10 text-[#059669] hover:bg-[#059669]/20">
                      {testimonial.service}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Quote Form */}
      <section id="quote" className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-[#1e3a8a] mb-4">Get Your Free Quote</h2>
              <p className="text-xl text-gray-600">Tell us about your project and schedule a home visit</p>
            </div>
            
            <Card className="border-2">
              <CardContent className="p-8">
                <form onSubmit={handleQuoteSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="name">Full Name *</Label>
                      <Input 
                        id="name"
                        value={quoteForm.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        required
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email">Email *</Label>
                      <Input 
                        id="email"
                        type="email"
                        value={quoteForm.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        required
                        className="mt-2"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="phone">Phone Number *</Label>
                      <Input 
                        id="phone"
                        type="tel"
                        value={quoteForm.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        required
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label htmlFor="service">Service Needed *</Label>
                      <select
                        id="service"
                        value={quoteForm.service}
                        onChange={(e) => handleInputChange('service', e.target.value)}
                        required
                        className="mt-2 flex h-9 w-full items-center justify-between whitespace-nowrap rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <option value="">Select service</option>
                        {services.map(service => (
                          <option key={service.id} value={service.title}>
                            {service.title}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="projectSize">Project Size/Details</Label>
                      <Input 
                        id="projectSize"
                        value={quoteForm.projectSize}
                        onChange={(e) => handleInputChange('projectSize', e.target.value)}
                        placeholder="e.g., 10 doors, full kitchen, etc."
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label htmlFor="appointment">Preferred Home Visit Date</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            id="appointment"
                            variant="outline"
                            className="w-full mt-2 justify-start text-left font-normal"
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {quoteForm.appointmentDate ? format(quoteForm.appointmentDate, 'PPP') : <span>Pick a date</span>}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={quoteForm.appointmentDate}
                            onSelect={(date) => handleInputChange('appointmentDate', date)}
                            disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="message">Additional Details</Label>
                    <Textarea 
                      id="message"
                      value={quoteForm.message}
                      onChange={(e) => handleInputChange('message', e.target.value)}
                      rows={4}
                      placeholder="Tell us more about your project..."
                      className="mt-2"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="photos">Upload Photos (Optional)</Label>
                    <p className="text-sm text-gray-500 mt-1 mb-2">Upload up to 7 photos of your current cabinets (5MB max each)</p>
                    <Input 
                      id="photos"
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/heic,image/heif"
                      multiple
                      onChange={handlePhotoChange}
                      className="mt-2"
                    />
                    {photos.length > 0 && (
                      <div className="mt-3 space-y-2">
                        <p className="text-sm font-medium text-gray-700">{photos.length} photo(s) selected:</p>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                          {photos.map((photo, index) => (
                            <div key={index} className="relative bg-gray-100 p-2 rounded-md text-xs flex items-center justify-between">
                              <span className="truncate flex-1">{photo.name}</span>
                              <button
                                type="button"
                                onClick={() => removePhoto(index)}
                                className="ml-2 text-red-500 hover:text-red-700 font-bold"
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <Button 
                    type="submit" 
                    size="lg" 
                    disabled={isSubmitting}
                    className="w-full bg-[#059669] hover:bg-[#047857] text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Sending...' : 'Submit Quote Request'} 
                    {!isSubmitting && <ArrowRight className="ml-2" />}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-[#1e3a8a] mb-4">Frequently Asked Questions</h2>
              <p className="text-xl text-gray-600">Everything you need to know about our services</p>
            </div>
            
            <Accordion type="single" collapsible className="space-y-4">
              <AccordionItem value="item-1" className="bg-white border-2 rounded-lg px-6">
                <AccordionTrigger className="text-left font-semibold text-[#1e3a8a] hover:text-[#059669] hover:no-underline">
                  How long does a typical cabinet refacing project take?
                </AccordionTrigger>
                <AccordionContent className="text-gray-600">
                  Most cabinet refacing projects take 3-5 days from start to finish. This includes measurement, CNC cutting, painting/finishing, and installation. We'll provide a specific timeline during your consultation.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-2" className="bg-white border-2 rounded-lg px-6">
                <AccordionTrigger className="text-left font-semibold text-[#1e3a8a] hover:text-[#059669] hover:no-underline">
                  What's the difference between refacing and replacing cabinets?
                </AccordionTrigger>
                <AccordionContent className="text-gray-600">
                  Refacing involves keeping your existing cabinet boxes and replacing doors, drawer fronts, and hardware. It's more affordable and faster. Replacement means completely new cabinets, which is necessary if your cabinet boxes are damaged or you want to change the layout.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-3" className="bg-white border-2 rounded-lg px-6">
                <AccordionTrigger className="text-left font-semibold text-[#1e3a8a] hover:text-[#059669] hover:no-underline">
                  Do you offer financing options?
                </AccordionTrigger>
                <AccordionContent className="text-gray-600">
                  Yes! We partner with several financing companies to offer flexible payment plans. Contact us for details about current rates and terms that fit your budget.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-4" className="bg-white border-2 rounded-lg px-6">
                <AccordionTrigger className="text-left font-semibold text-[#1e3a8a] hover:text-[#059669] hover:no-underline">
                  What makes your CNC process better?
                </AccordionTrigger>
                <AccordionContent className="text-gray-600">
                  Our CNC router ensures perfect precision and repeatability. Every cut is exactly to specification, resulting in doors and panels that fit perfectly. This technology also allows us to create custom designs and intricate patterns that would be impossible with traditional methods.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-5" className="bg-white border-2 rounded-lg px-6">
                <AccordionTrigger className="text-left font-semibold text-[#1e3a8a] hover:text-[#059669] hover:no-underline">
                  What's your service area?
                </AccordionTrigger>
                <AccordionContent className="text-gray-600">
                  We proudly serve Metro Detroit and surrounding areas including Oakland, Macomb, and Wayne counties. Contact us to confirm service availability in your specific location.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1e3a8a] text-white py-12">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <img src="https://customer-assets.emergentagent.com/job_cabinet-hero/artifacts/3942icok_image.png" alt="Cabinet Hero" className="h-12 w-12" />
                <span className="text-xl font-bold">Cabinet Hero</span>
              </div>
              <p className="text-gray-300">Precision-crafted cabinet solutions. Repair. Replace. Refinish.</p>
            </div>
            
            <div>
              <h3 className="font-semibold text-lg mb-4">Services</h3>
              <ul className="space-y-2 text-gray-300">
                <li><a href="#services" className="hover:text-[#059669] transition-colors">Cabinet Refacing</a></li>
                <li><a href="#services" className="hover:text-[#059669] transition-colors">Cabinet Refinishing</a></li>
                <li><a href="#services" className="hover:text-[#059669] transition-colors">Cabinet Replacement</a></li>
                <li><a href="#services" className="hover:text-[#059669] transition-colors">Cabinet Repair</a></li>
                <li><a href="#services" className="hover:text-[#059669] transition-colors">Cabinet Adjustments</a></li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold text-lg mb-4">Quick Links</h3>
              <ul className="space-y-2 text-gray-300">
                <li><a href="#gallery" className="hover:text-[#059669] transition-colors">Gallery</a></li>
                <li><a href="#quote" className="hover:text-[#059669] transition-colors">Get Quote</a></li>
                <li><a href="/admin" className="hover:text-[#059669] transition-colors">Admin Login</a></li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold text-lg mb-4">Contact Us</h3>
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  <span>(248) 383-5867</span>
                </li>
                <li className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  <span>lance@mycabinethero.com</span>
                </li>
                <li className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  <span>932 W 14 Mile Rd, Clawson, MI 48017</span>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-white/20 mt-12 pt-8 text-center text-gray-300">
            <p>&copy; 2025 Cabinet Hero. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;